import useGroupMetricsNew from '../useGroupMetricsNew/useGroupMetricsNew';
import { useSelector } from 'react-redux';
import {
  selectedPeriodKeySelector,
  uiStateSelector
} from 'state/selectors/ui-state';
import { getGranularityByDates } from '../../../../performance/views/CustomReport/custom-report.util';
import { getLastPeriodDates } from '../../GroupMetricsTable/GroupMetricsTable.service';
import { GRANULARITY } from '@linearb/linjs-common';
import { prevIterationSelector } from 'state/selectors/iterations-picker';
import { GRANULARITY_TO_METRICS_V2_GRANULARITY } from '../../../../performance/views/CustomReport/consts';
import { useMemo, useState } from 'react';
import uniq from 'lodash/uniq';
import { getMetricsNamesToFetch } from '../../groupDashboard.service';

const useFetchGroupTableMetrics = ({
  setAggregatedMetrics,
  setMetricsPerGranularity,
  setAggregatedLastPeriodMetrics,
  setMetricsPerGranularityLastPeriod,
  groupsManagedPages,
  tableMetricsNames = []
}) => {
  const { dates } = useSelector(uiStateSelector);
  const selectedPeriodKey = useSelector(selectedPeriodKeySelector);
  const prevIteration = useSelector(prevIterationSelector);
  const granularity = getGranularityByDates({
    start_date: dates.from,
    end_date: dates.to,
    selectedGranularity: GRANULARITY.AUTO
  });
  const lastPeriodDates = getLastPeriodDates(selectedPeriodKey, prevIteration);
  const granularityLastPeriod = lastPeriodDates
    ? getGranularityByDates({
        start_date: lastPeriodDates.from,
        end_date: lastPeriodDates.to,
        selectedGranularity: GRANULARITY.AUTO
      })
    : GRANULARITY.AUTO;
  const metricsNames = useMemo(
    () => uniq(getMetricsNamesToFetch(tableMetricsNames)),
    [tableMetricsNames]
  );

  const [isExpanding, setIsExpanding] = useState({
    aggregated: false,
    perGranularity: false,
    lastPeriod: false,
    lastPeriodPerGranularity: false
  });

  const { isLoading: aggregatedMetricsLoading, debouncedFetchGroupsMetrics } =
    useGroupMetricsNew({
      dates,
      setMetrics: setAggregatedMetrics,
      managedPages: groupsManagedPages,
      metricsNames,
      showToastOnError: true,
      setIsExpanding: val =>
        setIsExpanding(prev => ({ ...prev, aggregated: val }))
    });

  const {
    isLoading: metricsPerGranularityLoading,
    debouncedFetchGroupsMetrics: debouncedFetchGroupsMetricsPerGranularity
  } = useGroupMetricsNew({
    dates,
    roll_up: GRANULARITY_TO_METRICS_V2_GRANULARITY[granularity],
    setMetrics: setMetricsPerGranularity,
    managedPages: groupsManagedPages,
    metricsNames,
    setIsExpanding: val =>
      setIsExpanding(prev => ({ ...prev, perGranularity: val }))
  });

  // last period data - for the arrows in the table
  const {
    isLoading: aggregatedLastPeriodMetricsLoading,
    debouncedFetchGroupsMetrics: debouncedFetchGroupsAggregatedMetricsLastPeriod
  } = useGroupMetricsNew({
    dates: lastPeriodDates,
    setMetrics: setAggregatedLastPeriodMetrics,
    managedPages: groupsManagedPages,
    metricsNames,
    setIsExpanding: val =>
      setIsExpanding(prev => ({ ...prev, lastPeriod: val }))
  });

  const {
    isLoading: metricsPerGranularityLastPeriodLoading,
    debouncedFetchGroupsMetrics:
      debouncedFetchGroupMetricsPerGranularityLastPeriod
  } = useGroupMetricsNew({
    dates: lastPeriodDates,
    roll_up: GRANULARITY_TO_METRICS_V2_GRANULARITY[granularityLastPeriod],
    setMetrics: setMetricsPerGranularityLastPeriod,
    managedPages: groupsManagedPages,
    metricsNames,
    setIsExpanding: val =>
      setIsExpanding(prev => ({ ...prev, lastPeriodPerGranularity: val }))
  });

  const fetchMetrics = teamIds => {
    setIsExpanding({
      aggregated: true,
      perGranularity: true,
      lastPeriod: true,
      lastPeriodPerGranularity: true
    });
    debouncedFetchGroupsMetrics(teamIds);
    debouncedFetchGroupsMetricsPerGranularity(teamIds);
    debouncedFetchGroupsAggregatedMetricsLastPeriod(teamIds);
    debouncedFetchGroupMetricsPerGranularityLastPeriod(teamIds);
  };

  return {
    isLoadingTable:
      aggregatedMetricsLoading ||
      metricsPerGranularityLoading ||
      aggregatedLastPeriodMetricsLoading ||
      metricsPerGranularityLastPeriodLoading,
    isExpandingTable: Object.values(isExpanding).some(Boolean),
    fetchMetrics,
    lastPeriodDates
  };
};

export default useFetchGroupTableMetrics;
